/**
 * Shared utility functions for the community module.
 * Extracted to eliminate duplication across post cards, detail views, and feeds.
 */

import type { FeedParams } from "./types"

/** Format large numbers for display: 1200 → "1.2K", 15000 → "1.5W" */
export function formatCount(num: number | undefined): string {
  if (num == null) return "0"
  if (num >= 10000) return `${(num / 10000).toFixed(1)}W`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

/** Format a date string as relative time in Chinese */
export function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "刚刚"
  if (mins < 60) return `${mins}分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}小时前`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}天前`
  if (days < 30) return `${days}天前`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}个月前`
  return `${Math.floor(months / 12)}年前`
}

/** Strip Markdown syntax to produce a plain-text preview string */
export function stripMarkdown(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, "")         // headings
    .replace(/\*\*(.+?)\*\*/g, "$1")      // bold
    .replace(/\*(.+?)\*/g, "$1")          // italic
    .replace(/~~(.+?)~~/g, "$1")          // strikethrough
    .replace(/`{1,3}[^`]*`{1,3}/g, "")   // inline/block code
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // links & images
    .replace(/^>\s+/gm, "")              // blockquotes
    .replace(/^[-*+]\s+/gm, "")          // unordered list markers
    .replace(/^\d+\.\s+/gm, "")          // ordered list markers
    .replace(/\n{2,}/g, " ")             // collapse newlines
    .trim()
}

/** 根据 FeedParams 生成确定性 feed key（唯一定义，所有使用处引用此函数） */
export function buildFeedKey(params: FeedParams): string {
  const parts: string[] = [params.tab ?? 'recommended']
  if (params.tag) parts.push(`tag=${params.tag}`)
  if (params.boardSlug) parts.push(`board=${params.boardSlug}`)
  if (params.userId) parts.push(`user=${params.userId}`)
  if (params.abilityType) parts.push(`type=${params.abilityType}`)
  if (params.search) parts.push(`q=${params.search}`)
  if (params.sort) parts.push(`sort=${params.sort}`)
  if (params.sortDirection) parts.push(`dir=${params.sortDirection}`)
  return parts.join(':')
}
